import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Plus, Edit, RefreshCw, Smartphone, AlertTriangle, Trash2 } from 'lucide-react';
import { Topbar } from '@/components/layout/Topbar';
import { Dialog } from '@/components/ui/Dialog';
import { useAbonnes, useCreateAbonne, useUpdateAbonne, useDeleteAbonne, useHotspots } from '@/hooks/useTelecom';
import { STATUT_ABONNE_LABELS, type StatutAbonne, type Abonne } from '@/types';
import { formatNumber } from '@/utils/currency';
import { extractError } from '@/api/client';
import { useAuthStore } from '@/stores/authStore';
import { demander } from '@/components/ui/Fenetres';

const STATUT_BADGE: Record<StatutAbonne, string> = {
  actif:    'ds-bd ds-bd-s',
  expire:   'ds-bd ds-bd-w',
  suspendu: 'ds-bd ds-bd-d',
};

const fmtDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

export const AbonnesPage = () => {
  const filiale = useAuthStore((s) => s.filiale);
  const [statut, setStatut]         = useState<StatutAbonne | ''>('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing]       = useState<Abonne | undefined>();
  const [error, setError]           = useState<string | null>(null);

  const { data, isLoading, refetch } = useAbonnes({ statut: statut || undefined, limit: 200 });
  const abonnes = data?.data ?? [];
  const remove = useDeleteAbonne();

  const openCreate = () => { setEditing(undefined); setDialogOpen(true); };
  const openEdit   = (a: Abonne) => { setEditing(a); setDialogOpen(true); };

  const onRemove = async (id: string, msisdn: string) => {
    if (!(await demander.confirmer({ titre: `Supprimer l'abonné ${msisdn} ?`, confirmer: 'Supprimer', danger: true }))) return;
    setError(null);
    try { await remove.mutateAsync(id); }
    catch (err) { setError(extractError(err).message); }
  };

  const totalRevenuFcfa = abonnes.reduce((s, a) => s + Number(BigInt(a.totalDepenseCents)) / 100, 0);
  const totalSoldeDataGo = abonnes.reduce((s, a) => s + a.soldeDataMo, 0) / 1024;

  return (
    <>
      <Topbar
        title="Abonnés WiFi"
        subtitle={filiale ? `${abonnes.length} abonné(s) · ${filiale.fullName}` : 'Abonnés'}
        actions={
          <>
            <button className="ds-btn ds-btn-g ds-btn-sm" onClick={() => void refetch()}><RefreshCw size={14} className={isLoading ? 'animate-spin' : ''} /></button>
            <button className="ds-btn ds-btn-p" onClick={openCreate}><Plus size={14} /> Nouvel abonné</button>
          </>
        }
      />
      <div className="ds-content">
        {error && <div className="ds-alert-strip ds-alert-d" style={{ marginBottom: 16 }}><span className="ds-alert-ico"><AlertTriangle size={18} /></span><div className="ds-alert-txt">{error}</div></div>}

        <div className="ds-kpi-row" style={{ marginBottom: 16 }}>
          <div className="ds-kr kr-c1"><div className="ds-kr-ico">👥</div><div className="ds-kr-val">{abonnes.length}</div><div className="ds-kr-lbl">Abonnés</div></div>
          <div className="ds-kr kr-c3"><div className="ds-kr-ico">✅</div><div className="ds-kr-val">{abonnes.filter((a) => a.statut === 'actif').length}</div><div className="ds-kr-lbl">Actifs</div></div>
          <div className="ds-kr kr-c2"><div className="ds-kr-ico">📊</div><div className="ds-kr-val">{formatNumber(totalSoldeDataGo)}</div><div className="ds-kr-lbl">Total data restant (Go)</div></div>
          <div className="ds-kr kr-c4"><div className="ds-kr-ico">💰</div><div className="ds-kr-val">{formatNumber(totalRevenuFcfa)}</div><div className="ds-kr-lbl">Total dépensé (FCFA)</div></div>
        </div>

        <div className="ds-filters">
          <select className="ds-fi" value={statut} onChange={(e) => setStatut(e.target.value as StatutAbonne | '')} style={{ width: 'auto' }}>
            <option value="">Tous statuts</option>
            {(Object.keys(STATUT_ABONNE_LABELS) as StatutAbonne[]).map((s) => <option key={s} value={s}>{STATUT_ABONNE_LABELS[s]}</option>)}
          </select>
        </div>

        <div className="ds-table-panel">
          <div className="ds-table-header"><div><div className="ds-table-title">Abonnés</div><div className="ds-table-sub">{abonnes.length} abonné{abonnes.length > 1 ? 's' : ''}</div></div></div>
          <table className="ds-tbl">
            <thead><tr>
              <th style={{ width: 130 }}>MSISDN</th>
              <th>Nom / Email</th>
              <th>Plan</th>
              <th>Hotspot préféré</th>
              <th className="ds-am">Solde data</th>
              <th>Inscription</th>
              <th style={{ width: 90 }}>Statut</th>
              <th style={{ width: 80 }}></th>
            </tr></thead>
            <tbody>
              {isLoading && <tr><td colSpan={8} style={{ textAlign: 'center', padding: 32, color: 'var(--tx-3)' }}>Chargement…</td></tr>}
              {!isLoading && abonnes.length === 0 && (
                <tr><td colSpan={8} style={{ textAlign: 'center', padding: 48 }}>
                  <Smartphone size={28} style={{ margin: '0 auto 8px', color: 'var(--tx-3)' }} />
                  <p style={{ fontSize: 13, color: 'var(--tx-3)' }}>Aucun abonné</p>
                  <button className="ds-btn ds-btn-p ds-btn-sm" style={{ marginTop: 12 }} onClick={openCreate}><Plus size={12} /> Créer le premier abonné</button>
                </td></tr>
              )}
              {!isLoading && abonnes.map((a) => (
                <tr key={a.id}>
                  <td style={{ fontFamily: 'var(--font-m)', fontSize: 12, fontWeight: 600 }}>{a.msisdn}</td>
                  <td>
                    <div className="ds-entry-label">{a.nom ?? '—'}</div>
                    {a.email && <div className="ds-entry-sub">{a.email}</div>}
                  </td>
                  <td style={{ fontSize: 12 }}>{a.planAbonnement ?? '—'}</td>
                  <td style={{ fontSize: 12 }}>{a.hotspotPrefere ? `${a.hotspotPrefere.code} — ${a.hotspotPrefere.nom}` : '—'}</td>
                  <td className="ds-am" style={{ fontWeight: 600, color: 'var(--p)' }}>{formatNumber(a.soldeDataMo)} Mo</td>
                  <td style={{ fontSize: 12, color: 'var(--tx-2)' }}>{fmtDate(a.dateInscription)}</td>
                  <td><span className={STATUT_BADGE[a.statut]}>{STATUT_ABONNE_LABELS[a.statut]}</span></td>
                  <td><div style={{ display: 'flex', gap: 4 }}>
                    <button onClick={() => openEdit(a)} className="ds-row-btn" title="Modifier"><Edit size={13} /></button>
                    <button onClick={() => onRemove(a.id, a.msisdn)} className="ds-row-btn" title="Supprimer" disabled={remove.isPending}><Trash2 size={13} /></button>
                  </div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <AbonneFormDialog open={dialogOpen} onClose={() => setDialogOpen(false)} abonne={editing} />
    </>
  );
};

const AbonneFormDialog = ({ open, onClose, abonne }: { open: boolean; onClose: () => void; abonne?: Abonne }) => {
  const isEdit = !!abonne;
  const [msisdn, setMsisdn]         = useState('');
  const [nom, setNom]               = useState('');
  const [email, setEmail]           = useState('');
  const [hotspotId, setHotspotId]   = useState('');
  const [plan, setPlan]             = useState('');
  const [statut, setStatut]         = useState<StatutAbonne>('actif');
  const [soldeData, setSoldeData]   = useState('');
  const [notes, setNotes]           = useState('');
  const [error, setError]           = useState<string | null>(null);

  const { data: hotspotsData } = useHotspots({ statut: 'actif', limit: 500 });
  const hotspots = hotspotsData?.data ?? [];
  const create = useCreateAbonne();
  const update = useUpdateAbonne();

  useEffect(() => {
    if (!open) return;
    if (abonne) {
      setMsisdn(abonne.msisdn); setNom(abonne.nom ?? ''); setEmail(abonne.email ?? '');
      setHotspotId(abonne.hotspotPrefereId ?? ''); setPlan(abonne.planAbonnement ?? '');
      setStatut(abonne.statut); setSoldeData(abonne.soldeDataMo.toString()); setNotes(abonne.notes ?? '');
    } else {
      setMsisdn(''); setNom(''); setEmail(''); setHotspotId(''); setPlan('');
      setStatut('actif'); setSoldeData('0'); setNotes('');
    }
    setError(null);
  }, [open, abonne]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      if (isEdit && abonne) {
        await update.mutateAsync({ id: abonne.id, payload: {
          nom: nom.trim() || undefined, email: email.trim() || undefined,
          hotspotPrefereId: hotspotId || undefined, planAbonnement: plan.trim() || undefined,
          statut, soldeDataMo: soldeData ? parseInt(soldeData, 10) : undefined,
          notes: notes.trim() || undefined,
        }});
      } else {
        await create.mutateAsync({
          msisdn: msisdn.trim(),
          nom: nom.trim() || undefined, email: email.trim() || undefined,
          hotspotPrefereId: hotspotId || undefined, planAbonnement: plan.trim() || undefined,
          statut, soldeDataMo: soldeData ? parseInt(soldeData, 10) : 0,
          notes: notes.trim() || undefined,
        });
      }
      onClose();
    } catch (err) { setError(extractError(err).message); }
  };

  const canSubmit = isEdit ? true : /^[0-9]{8,15}$/.test(msisdn.trim());

  return (
    <Dialog open={open} onClose={onClose} title={isEdit ? `Modifier — ${abonne?.msisdn}` : 'Nouvel abonné WiFi'} subtitle="MSISDN format E.164 sans + (ex: 22507110000)" size="md">
      <form onSubmit={onSubmit} className="space-y-3">
        {error && <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700"><AlertTriangle className="inline size-3 mr-1" />{error}</div>}
        <div className="grid grid-cols-2 gap-3">
          <Field label="MSISDN *"><input value={msisdn} onChange={(e) => setMsisdn(e.target.value)} className="ds-fi" required disabled={isEdit} style={{ width: '100%', fontFamily: 'var(--font-m)' }} placeholder="22507110000" /></Field>
          <Field label="Statut"><select value={statut} onChange={(e) => setStatut(e.target.value as StatutAbonne)} className="ds-fi" style={{ width: '100%' }}>{(Object.keys(STATUT_ABONNE_LABELS) as StatutAbonne[]).map((s) => <option key={s} value={s}>{STATUT_ABONNE_LABELS[s]}</option>)}</select></Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Nom"><input value={nom} onChange={(e) => setNom(e.target.value)} className="ds-fi" style={{ width: '100%' }} placeholder="Koné Mamadou" /></Field>
          <Field label="Email"><input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="ds-fi" style={{ width: '100%' }} placeholder="abonne@example.ci" /></Field>
        </div>
        <Field label="Hotspot préféré">
          <select value={hotspotId} onChange={(e) => setHotspotId(e.target.value)} className="ds-fi" style={{ width: '100%' }}>
            <option value="">— Aucun —</option>
            {hotspots.map((h) => <option key={h.id} value={h.id}>{h.code} — {h.nom}</option>)}
          </select>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Plan abonnement"><input value={plan} onChange={(e) => setPlan(e.target.value)} className="ds-fi" style={{ width: '100%' }} placeholder="Découverte 1Go" /></Field>
          <Field label="Solde data (Mo)"><input type="number" min="0" value={soldeData} onChange={(e) => setSoldeData(e.target.value)} className="ds-fi" style={{ width: '100%' }} placeholder="0" /></Field>
        </div>
        <Field label="Notes"><textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="ds-fi" style={{ width: '100%' }} /></Field>
        <div className="flex justify-end gap-2 border-t border-gray-100 pt-3">
          <button type="button" className="ds-btn ds-btn-g" onClick={onClose}>Annuler</button>
          <button type="submit" className="ds-btn ds-btn-p" disabled={!canSubmit || create.isPending || update.isPending}>{isEdit ? 'Enregistrer' : 'Créer l\'abonné'}</button>
        </div>
      </form>
    </Dialog>
  );
};

const Field = ({ label, children }: { label: string; children: ReactNode }) => (
  <div><label className="mb-1 block text-xs font-medium text-gray-600">{label}</label>{children}</div>
);
